interface MetricBarProps {
  /** Percentage value from -100 to +100. 0 = midpoint, +100 = full right (green), -100 = full left (red) */
  percentage: number;
}

export function MetricBar({ percentage }: MetricBarProps) {
  // Clamp percentage to -100 to +100 range
  const clampedPercentage = Math.max(-100, Math.min(100, percentage));

  // Determine position relative to midpoint (0)
  const isAtMidpoint = clampedPercentage === 0;
  const isPositive = clampedPercentage > 0;

  // Calculate fill percentage (0-100%) for the appropriate half
  const fillPercent = `${Math.abs(clampedPercentage)}%`;

  return (
    <div className="relative w-full pt-2 pb-1">
      {/* Two-half layout: left (negative) | midpoint | right (positive) */}
      <div className="flex h-2">
        {/* Left half (negative values) */}
        <div className="relative flex-1 bg-gray-200 flex justify-end">
          {!isPositive && !isAtMidpoint && (
            <>
              {/* Cursor on negative side */}
              <div
                className="absolute top-0 -translate-y-full z-20"
                style={{
                  right: fillPercent,
                  transform: 'translateX(50%) translateY(-100%)',
                }}
              >
                <div className="w-0 h-0 border-l-[5px] border-r-[5px] border-t-[5px] border-l-transparent border-r-transparent border-t-black" />
              </div>
              {/* Red bar growing from right edge (midpoint) to left */}
              <div
                className="h-full bg-red-500 transition-all duration-300"
                style={{ width: fillPercent }}
              />
            </>
          )}
        </div>

        {/* Midpoint indicator */}
        <div className="relative w-0.5 bg-primary-700 -my-1 z-10">
          {/* Cursor at midpoint when value equals midpoint */}
          {isAtMidpoint && (
            <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-full z-20">
              <div className="w-0 h-0 border-l-[5px] border-r-[5px] border-t-[5px] border-l-transparent border-r-transparent border-t-black" />
            </div>
          )}
        </div>

        {/* Right half (positive values) */}
        <div className="relative flex-1 bg-gray-200">
          {isPositive && (
            <>
              {/* Cursor on positive side */}
              <div
                className="absolute top-0 -translate-y-full z-20"
                style={{
                  left: fillPercent,
                  transform: 'translateX(-50%) translateY(-100%)',
                }}
              >
                <div className="w-0 h-0 border-l-[5px] border-r-[5px] border-t-[5px] border-l-transparent border-r-transparent border-t-black" />
              </div>
              {/* Green bar growing from left edge (midpoint) to right */}
              <div
                className="h-full bg-green-500 transition-all duration-300"
                style={{ width: fillPercent }}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
